import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { meQuery } from "./auth.ts";
import { DeskPage } from "./desk/DeskPage.tsx";
import { deskQuery } from "./desk/desk.ts";
import { LoginPage } from "./login/LoginPage.tsx";
import { NotFound, ServerError } from "./ErrorPages.tsx";
import { ScanPage } from "./scan/ScanPage.tsx";
import { SectionPage } from "./SectionPage.tsx";
import { Shell } from "./shell/Shell.tsx";
import { UiKitPage } from "./ui-kit/UiKitPage.tsx";
import { IssuePage } from "./stock/IssuePage.tsx";
import { ProductPage } from "./stock/ProductPage.tsx";
import { RequestDraftPage } from "./stock/RequestDraftPage.tsx";
import { RequestsPage } from "./stock/RequestsPage.tsx";
import { ShelfPage, type ShelfMode } from "./stock/ShelfPage.tsx";
import { StockHub } from "./stock/StockHub.tsx";
import { TodayPage, type TodayTab } from "./stock/TodayPage.tsx";
import type { WarehouseCode } from "./stock/types.ts";
import { OrderPage } from "./purchasing/OrderPage.tsx";
import { LinePage } from "./production/LinePage.tsx";
import { PartnerPage, PartnersPage } from "./catalog/PartnersPage.tsx";
import { NewProductPage, ProductsPage } from "./catalog/ProductsPage.tsx";
import { TemplatePage } from "./catalog/TemplatePage.tsx";
import { MoPage } from "./production/MoPage.tsx";
import { MyOpsPage } from "./production/MyOpsPage.tsx";
import { UnitPage } from "./production/UnitPage.tsx";
import { PurchasingPage, type PurchasingTab } from "./purchasing/PurchasingPage.tsx";
import { SalesOrderPage } from "./sales/SalesOrderPage.tsx";
import { SalesPage } from "./sales/SalesPage.tsx";
import { RepairPage } from "./service/RepairPage.tsx";
import { MonthClosePage } from "./costing/MonthClosePage.tsx";
import { PnlPage } from "./costing/PnlPage.tsx";
import { PricesPage } from "./costing/PricesPage.tsx";

interface Context {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<Context>()({
  component: Outlet,
  notFoundComponent: NotFound,
});

export interface LoginSearch {
  error?: string;
  out?: 1;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    error: typeof s.error === "string" ? s.error : undefined,
    out: s.out === 1 || s.out === "1" ? 1 : undefined,
  }),
  component: LoginPage,
});

/** Всё, что за входом: оболочка и экраны. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    // Отказ входа сервер отдаёт на /?login_error=<код> (поток B, OIDC).
    const loginError = (location.search as Record<string, unknown>).login_error;
    if (typeof loginError === "string") throw redirect({ to: "/login", search: { error: loginError } });
    const me = await context.queryClient.query(meQuery);
    if (!me) throw redirect({ to: "/login", search: {} });
    return { me };
  },
  component: Shell,
  errorComponent: ServerError,
});

const deskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  loader: ({ context }) => context.queryClient.query(deskQuery),
  component: DeskPage,
});

const sectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/s/$sectionId",
  component: SectionPage,
});

const scanRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/scan/$code",
  component: ScanPage,
});

const uiKitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ui",
  component: UiKitPage,
});

const oneOf = <T extends string>(v: unknown, values: readonly T[]): T | undefined =>
  typeof v === "string" && (values as readonly string[]).includes(v) ? (v as T) : undefined;

const WAREHOUSE_CODES = ["MAIN", "SER", "BPK", "RND", "SRV", "FG"] as const;

const stockRoute = createRoute({ getParentRoute: () => appRoute, path: "/stock", component: StockHub });

const shelfRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/shelf",
  validateSearch: (s: Record<string, unknown>): { code?: string; mode?: ShelfMode } => ({
    code: typeof s.code === "string" && s.code ? s.code : undefined,
    mode: oneOf(s.mode, ["receive", "issue", "count"] as const),
  }),
  component: ShelfPage,
});

const productRoute = createRoute({ getParentRoute: () => appRoute, path: "/stock/product/$sku", component: ProductPage });

const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/today",
  validateSearch: (s: Record<string, unknown>): { tab?: TodayTab } => ({ tab: oneOf(s.tab, ["receive", "issue", "count"] as const) }),
  component: TodayPage,
});

const issueRoute = createRoute({ getParentRoute: () => appRoute, path: "/stock/issue/$id", component: IssuePage });

const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/requests",
  validateSearch: (s: Record<string, unknown>): { warehouse?: WarehouseCode } => ({ warehouse: oneOf(s.warehouse, WAREHOUSE_CODES) }),
  component: RequestsPage,
});

const requestNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stock/requests/new",
  validateSearch: (s: Record<string, unknown>): { warehouse?: WarehouseCode } => ({ warehouse: oneOf(s.warehouse, WAREHOUSE_CODES) }),
  component: RequestDraftPage,
});

const purchasingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchasing",
  validateSearch: (s: Record<string, unknown>): { tab?: PurchasingTab } => ({ tab: oneOf(s.tab, ["shortage", "expected", "drafts"] as const) }),
  component: PurchasingPage,
});

const orderRoute = createRoute({ getParentRoute: () => appRoute, path: "/purchasing/orders/$id", component: OrderPage });

const serialSearch = (s: Record<string, unknown>): { serial?: string } => ({
  serial: typeof s.serial === "string" && s.serial ? s.serial : undefined,
});

const lineRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/line", component: LinePage });
const moRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/orders/$id", component: MoPage });
const myOpsRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/my", validateSearch: serialSearch, component: MyOpsPage });
const unitRoute = createRoute({ getParentRoute: () => appRoute, path: "/production/unit", validateSearch: serialSearch, component: UnitPage });

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products",
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({ q: typeof s.q === "string" && s.q ? s.q : undefined }),
  component: ProductsPage,
});
const newProductRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/products/new", component: NewProductPage });
const templateRoute = createRoute({ getParentRoute: () => appRoute, path: "/catalog/templates/$id", component: TemplatePage });
const partnersRoute = createRoute({ getParentRoute: () => appRoute, path: "/partners", component: PartnersPage });
const partnerRoute = createRoute({ getParentRoute: () => appRoute, path: "/partners/$id", component: PartnerPage });

const salesRoute = createRoute({ getParentRoute: () => appRoute, path: "/sales", component: SalesPage });
const salesOrderRoute = createRoute({ getParentRoute: () => appRoute, path: "/sales/orders/$id", component: SalesOrderPage });
const repairRoute = createRoute({ getParentRoute: () => appRoute, path: "/service/repairs/$id", component: RepairPage });

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const monthSearch = (s: Record<string, unknown>): { month?: string } => ({ month: str(s.month) });
const closeRoute = createRoute({ getParentRoute: () => appRoute, path: "/costing/close", validateSearch: monthSearch, component: MonthClosePage });
const pnlRoute = createRoute({ getParentRoute: () => appRoute, path: "/costing/pnl", validateSearch: monthSearch, component: PnlPage });
const pricesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/costing/prices",
  validateSearch: (s: Record<string, unknown>): { product?: string; serial?: string } => ({ product: str(s.product), serial: str(s.serial) }),
  component: PricesPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    deskRoute,
    sectionRoute,
    scanRoute,
    uiKitRoute,
    stockRoute,
    shelfRoute,
    productRoute,
    todayRoute,
    issueRoute,
    requestsRoute,
    requestNewRoute,
    purchasingRoute,
    orderRoute,
    lineRoute,
    moRoute,
    myOpsRoute,
    unitRoute,
    productsRoute,
    newProductRoute,
    templateRoute,
    partnersRoute,
    partnerRoute,
    salesRoute,
    salesOrderRoute,
    repairRoute,
    closeRoute,
    pnlRoute,
    pricesRoute,
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    // Данные держит Query, у роутера свой кэш не нужен.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
